'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

const AnimationContext = createContext({
    shouldAnimate: false,
    isMobile: false
});

export function AnimationProvider({ children }) {
    const [isMobile, setIsMobile] = useState(false);
    const [isNetworkIdle, setIsNetworkIdle] = useState(false);

    useEffect(() => {
        // Check for mobile device (standard lg breakpoint is 1024px)
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        // Initial check
        checkMobile();

        // Listen for resize
        window.addEventListener('resize', checkMobile);

        // Network Idle Check
        // We use window.onload as a baseline for "page loaded"
        // and add a small delay to ensure main thread is clear
        const handleLoad = () => {
            // Using requestIdleCallback if available, otherwise setTimeout
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => {
                    setIsNetworkIdle(true);
                }, { timeout: 2000 });
            } else {
                setTimeout(() => {
                    setIsNetworkIdle(true);
                }, 500);
            }
        };

        if (document.readyState === 'complete') {
            handleLoad();
        } else {
            window.addEventListener('load', handleLoad);
        }

        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('load', handleLoad);
        };
    }, []);

    // Logic:
    // 1. If mobile: shouldAnimate is FALSE (disable animations, show content immediately)
    // 2. If desktop:
    //    - If network NOT idle: shouldAnimate is FALSE (wait)
    //    - If network idle: shouldAnimate is TRUE (start animations)
    // Exception: For desktop, we might want to start "showing" things, but 
    // the requirement is "start only when network is idle".
    // 
    // However, for Framer Motion, "shouldAnimate = false" typically means "don't run animation properties".
    // If we want components to be VISIBLE but STATIC on mobile, we need to handle that in the components.
    // If we want components to be HIDDEN on desktop until idle, we handle that here.

    // Adjusted Logic:
    // Mobile: Always "ready" but maybe strict animations are disabled depending on usage.
    // For this specific requirement "do not add animations... keep animations for desktop... start [desktop] only when network is idle":

    // We will export `shouldAnimate` which effectively means "Safe to run rich animations".
    // Components need to know:
    // - showing content immediately (Mobile) vs waiting (Desktop)?
    // The requirement says "start only when network is idle". This implies potential delay.

    const shouldAnimate = !isMobile && isNetworkIdle;

    return (
        <AnimationContext.Provider value={{ shouldAnimate, isMobile }}>
            {children}
        </AnimationContext.Provider>
    );
}

export function useAnimation() {
    return useContext(AnimationContext);
}
